import React, { ReactNode, useState } from 'react';

import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmButtonType?: ButtonType;
  cancelButtonType?: ButtonType;
  onConfirm: () => Promise<unknown> | unknown;
}

// Generic confirmation dialog. The modal closes automatically on successful
// confirm; on thrown error it stays open so the caller can surface it.
export const ConfirmModal = ({
  isOpen,
  onClose,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmButtonType = ButtonType.primary,
  cancelButtonType = ButtonType.tertiary,
  onConfirm,
}: Props): JSX.Element => {
  const [submitting, setSubmitting] = useState(false);

  const run = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className='w-full max-w-md'
      title={title}
      footer={
        <>
          <Button buttonType={cancelButtonType} onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            buttonType={confirmButtonType}
            disabled={submitting}
            onClick={run}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
};
